import React, { useState } from 'react';

import dayjs from '~/timeInit';

import DownloadIcon from '@mui/icons-material/Download';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';

import PageSubheader from '~/components/Common/Elements/PageSubheader';

import {
  ConnectivityEmailListParams,
  useExportConnectivityEmailProcessing,
  useGetConnectivityEmailProcessing,
  useGetConnectivityEmailProcessingBody,
} from '~/hooks/apis/connectivity/useConnectivityEmailProcessing';

import { downloadCsvFile } from '~/utils/csvDownload';

const PAGE_SIZE = 50;
const EXPORT_FILENAME_DATE_FORMAT = 'YYYY-MM-DD-HHmm';

const EXPORT_COLUMNS = [
  'Case ID',
  'Subject',
  'From',
  'Classification',
  'Classification reasoning',
  'Action taken',
  'Status',
  'Created',
  'Salesforce email message ID',
  'Job ID',
  'Email body',
];

const ACTION_TAKEN_LABELS: Record<string, string> = {
  extranet_reply_sent: 'Extranet reply sent',
  partial_extranet_reply_sent: 'Partial Extranet reply sent',
  case_closed: 'Case closed',
  none: 'No action',
};

const STATUS_COLOURS: Record<string, string> = {
  completed: 'success.main',
  processing: 'warning.main',
  abandoned: 'text.disabled',
};

function buildDateFilters(createdFromInput: string, createdToInput: string) {
  return {
    created_from: createdFromInput ? dayjs(createdFromInput).startOf('day').toISOString() : undefined,
    created_to: createdToInput ? dayjs(createdToInput).endOf('day').toISOString() : undefined,
  };
}

export default function ConnectivityEmailsPage() {
  const [caseIdInput, setCaseIdInput] = useState('');
  const [actionTakenInput, setActionTakenInput] = useState('');
  const [createdFromInput, setCreatedFromInput] = useState('');
  const [createdToInput, setCreatedToInput] = useState('');
  const [expandedRecordId, setExpandedRecordId] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exportWarning, setExportWarning] = useState<string | null>(null);
  const [queryParams, setQueryParams] = useState<ConnectivityEmailListParams>({ page: 1, limit: PAGE_SIZE });

  const { data, isLoading, isError } = useGetConnectivityEmailProcessing(queryParams);
  const {
    data: bodyData,
    isError: isBodyError,
    isFetching: isBodyFetching,
  } = useGetConnectivityEmailProcessingBody(expandedRecordId);
  const exportMutation = useExportConnectivityEmailProcessing();

  const records = data?.result?.records ?? [];
  const total = data?.result?.total ?? 0;
  const page = queryParams.page ?? 1;
  const isExporting = exportMutation.isPending;

  const getFilterInputs = () => ({
    sf_case_id: caseIdInput.trim() || undefined,
    action_taken: actionTakenInput || undefined,
    ...buildDateFilters(createdFromInput, createdToInput),
  });

  const getAppliedFilters = () => ({
    sf_case_id: queryParams.sf_case_id,
    action_taken: queryParams.action_taken,
    created_from: queryParams.created_from,
    created_to: queryParams.created_to,
  });

  const handleSearch = () => {
    setExpandedRecordId(null);
    setExportError(null);
    setExportWarning(null);
    setQueryParams({
      ...getFilterInputs(),
      page: 1,
      limit: PAGE_SIZE,
    });
  };

  const handleClear = () => {
    setCaseIdInput('');
    setActionTakenInput('');
    setCreatedFromInput('');
    setCreatedToInput('');
    setExpandedRecordId(null);
    setExportError(null);
    setExportWarning(null);
    setQueryParams({ page: 1, limit: PAGE_SIZE });
  };

  const handleToggleBody = (recordId: string) => {
    setExpandedRecordId((prev) => (prev === recordId ? null : recordId));
  };

  const handleExport = async () => {
    setExportError(null);
    setExportWarning(null);

    try {
      const response = await exportMutation.mutateAsync(getAppliedFilters());
      const exportRecords = response?.result?.records ?? [];
      const exportTotal = response?.result?.total ?? exportRecords.length;

      if (exportRecords.length === 0) {
        setExportWarning('No records matched the current filters.');
        return;
      }

      const rows = exportRecords.map((row) => ({
        'Case ID': row.sf_case_id,
        Subject: row.subject ?? '',
        From: row.from_address ?? '',
        Classification: row.classification ?? '',
        'Classification reasoning': row.classification_reasoning ?? '',
        'Action taken': row.action_taken ? (ACTION_TAKEN_LABELS[row.action_taken] ?? row.action_taken) : '',
        Status: row.status,
        Created: dayjs(row.created_at).format('YYYY-MM-DD HH:mm:ss'),
        'Salesforce email message ID': row.sf_email_message_id,
        'Job ID': row.job_id ?? '',
        'Email body': row.text_body ?? '',
      }));

      downloadCsvFile(
        rows,
        EXPORT_COLUMNS,
        `connectivity-email-processing-${dayjs().format(EXPORT_FILENAME_DATE_FORMAT)}.csv`,
      );

      if (exportTotal > exportRecords.length) {
        setExportWarning(`Exported ${exportRecords.length} of ${exportTotal} matching records.`);
      }
    } catch {
      setExportError('Failed to export records.');
    }
  };

  return (
    <Box>
      <PageSubheader title="Connectivity Emails" />

      <Stack direction="row" spacing={2} alignItems="center" mt={2} mb={3} flexWrap="wrap">
        <TextField
          label="Case ID"
          placeholder="5002y000009TESTAA1"
          value={caseIdInput}
          onChange={(e) => setCaseIdInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          size="small"
          sx={{ minWidth: 240 }}
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Action taken</InputLabel>
          <Select label="Action taken" value={actionTakenInput} onChange={(e) => setActionTakenInput(e.target.value)}>
            <MenuItem value="">
              <em>Any</em>
            </MenuItem>
            {Object.entries(ACTION_TAKEN_LABELS).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          InputLabelProps={{ shrink: true }}
          label="Created from"
          onChange={(e) => setCreatedFromInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          size="small"
          sx={{ minWidth: 170 }}
          type="date"
          value={createdFromInput}
        />

        <TextField
          InputLabelProps={{ shrink: true }}
          label="Created to"
          onChange={(e) => setCreatedToInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          size="small"
          sx={{ minWidth: 170 }}
          type="date"
          value={createdToInput}
        />

        <Button variant="contained" onClick={handleSearch}>
          Search
        </Button>

        <Button variant="text" onClick={handleClear}>
          Clear
        </Button>

        <Button
          disabled={isExporting}
          onClick={handleExport}
          startIcon={isExporting ? <CircularProgress size={16} /> : <DownloadIcon fontSize="small" />}
          variant="outlined"
        >
          Download XLSX
        </Button>

        {isLoading && <CircularProgress size={20} />}
      </Stack>

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load records.
        </Alert>
      )}
      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {exportError}
        </Alert>
      )}
      {exportWarning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {exportWarning}
        </Alert>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Case ID</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>From</TableCell>
              <TableCell>Classification</TableCell>
              <TableCell>Action taken</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Created</TableCell>
              <TableCell align="center">Body</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {records.length === 0 && !isLoading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    No records found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {records.map((row) => {
              const isExpanded = expandedRecordId === row.id;
              const body = isExpanded ? bodyData?.result?.text_body : undefined;
              const isBodyLoading = isExpanded && isBodyFetching;
              const hasBodyError = isExpanded && isBodyError;

              return (
                <React.Fragment key={row.id}>
                  <TableRow hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {row.sf_case_id}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography variant="body2" noWrap title={row.subject ?? undefined}>
                        {row.subject ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.from_address ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{row.classification ?? '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      {row.action_taken ? (ACTION_TAKEN_LABELS[row.action_taken] ?? row.action_taken) : '—'}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={STATUS_COLOURS[row.status] ?? 'text.primary'} fontWeight={500}>
                        {row.status}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {dayjs(row.created_at).format('D MMM YYYY HH:mm')}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        aria-label={isExpanded ? 'Hide email body' : 'Show email body'}
                        onClick={() => handleToggleBody(row.id)}
                        size="small"
                      >
                        {isExpanded ? (
                          <KeyboardArrowDownIcon fontSize="small" />
                        ) : (
                          <KeyboardArrowRightIcon fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  {isExpanded && (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        sx={{ bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}
                      >
                        <Typography
                          component="pre"
                          variant="body2"
                          sx={{
                            fontFamily: 'monospace',
                            m: 0,
                            maxHeight: 360,
                            overflow: 'auto',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          }}
                        >
                          {isBodyLoading && 'Loading email body...'}
                          {hasBodyError && 'Failed to load email body.'}
                          {!isBodyLoading && !hasBodyError && (body ?? 'No email body stored.')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[PAGE_SIZE]}
        onPageChange={(_, p) => setQueryParams((prev) => ({ ...prev, page: p + 1 }))}
      />
    </Box>
  );
}
